import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import api from '../api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState('verifying'); // 'verifying' | 'success' | 'expired' | 'already_verified' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('No verification token found in the link.'); return; }
    verify();
  }, [token]);

  async function verify() {
    try {
      const { data } = await api.get(`/auth/verify-email?token=${token}`);
      if (data.success) {
        // Log the user in immediately — redirect to onboarding for new accounts
        login(data.token, data.company);
        setState('success');
        setTimeout(() => navigate('/onboarding'), 2500);
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.error === 'already_verified') setState('already_verified');
      else if (errData?.error === 'expired') setState('expired');
      else { setState('error'); setErrorMsg(errData?.error || 'Verification failed.'); }
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a2535 0%, #1a3a5c 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const cardStyle = {
    background: '#fff', borderRadius: 16, padding: 40, width: '100%', maxWidth: 440,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
  };
  const btnStyle = {
    display: 'block', padding: '13px', background: '#1a2535', color: '#fff',
    borderRadius: 8, textDecoration: 'none', fontSize: 14, fontWeight: 700, marginBottom: 12,
  };

  const Logo = () => (
    <div style={{ marginBottom: 28, textAlign: 'center' }}>
      <img src="/jobhub-logo-dark.png" alt="JobHub" style={{ height: 100, width: 'auto', objectFit: 'contain' }} />
    </div>
  );

  return (
    <div style={containerStyle}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <Logo />
        <div style={cardStyle}>

          {/* Verifying spinner */}
          {state === 'verifying' && (
            <>
              <div style={{ width: 56, height: 56, border: '4px solid #e5e7eb', borderTop: '4px solid #1a2535', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>Verifying your email...</h2>
              <p style={{ fontSize: 14, color: '#6b7280' }}>Just a moment while we confirm your address.</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </>
          )}

          {/* Success */}
          {state === 'success' && (
            <>
              <div style={{ width: 68, height: 68, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 20px' }}>✅</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>Email verified!</h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 8px' }}>
                Your email has been confirmed. Welcome to Jobhub!
              </p>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>Setting up your account...</p>
              <div style={{ width: '100%', height: 4, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#1a2535', borderRadius: 2, animation: 'progress 2.5s linear forwards' }} />
              </div>
              <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes progress { from { width: 0% } to { width: 100% } }
              `}</style>
            </>
          )}

          {/* Already verified */}
          {state === 'already_verified' && (
            <>
              <div style={{ width: 68, height: 68, background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 20px' }}>ℹ️</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>Already verified</h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
                This email address has already been verified. You can sign in to your account.
              </p>
              <Link to="/login" style={btnStyle}>Sign in to Jobhub</Link>
            </>
          )}

          {/* Expired */}
          {state === 'expired' && (
            <>
              <div style={{ width: 68, height: 68, background: '#fef9c3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 20px' }}>⏱</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>Link expired</h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
                This verification link has expired. Verification links are valid for 24 hours.
                Request a new one from the login page.
              </p>
              <Link to="/login" style={btnStyle}>Go to sign in page</Link>
              <p style={{ fontSize: 12, color: '#9ca3af' }}>
                You can request a new verification email from the login page.
              </p>
            </>
          )}

          {/* Generic error */}
          {state === 'error' && (
            <>
              <div style={{ width: 68, height: 68, background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 20px' }}>❌</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>Verification failed</h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
                {errorMsg || 'Something went wrong with this verification link.'}
              </p>
              <Link to="/login" style={btnStyle}>Back to sign in</Link>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
